import {
    AddDemeterUserRequestMarketExpirations,
    DemeterMarket,
    DemeterMarketAccessModel,
    DemeterMarketAccessStatus,
} from '../../../../../Generated/Raven-Demeter';
import formattingService from '../../../../Services/Formatting/FormattingService';

export type MarketAccessExpirationOption = 'permanent' | 'fourteenDays' | 'thirtyDays' | 'custom';

export interface MarketAccessExpiration {
    market: DemeterMarket;
    expirationOption: MarketAccessExpirationOption;
    expirationDate?: Date;
}

// An expired grant stays on record even though its sector has left the selection, so it is held apart from the editable periods.
export interface MarketAccessPastGrant {
    market: DemeterMarket;
    status: DemeterMarketAccessStatus;
    endDate?: Date;
}

const numberOfDaysByExpirationOption: { [key in MarketAccessExpirationOption]?: number } = {
    fourteenDays: 14,
    thirtyDays: 30,
};

export const maximumRecommendedGrantNumberOfDays = 365;

// Matches the threshold the users list already uses to call a trial account expiring.
export const expiringSoonNumberOfDays = 7;

const numberOfMillisecondsInDay = 24 * 60 * 60 * 1000;

export const startOfDay = (date: Date): Date => {
    const startOfDayDate = new Date(date.getTime());
    startOfDayDate.setHours(0, 0, 0, 0);

    return startOfDayDate;
};

export const startOfToday = (): Date => startOfDay(new Date());

export const addDaysToStartOfToday = (numberOfDays: number): Date => {
    const futureDate = startOfToday();
    futureDate.setDate(futureDate.getDate() + numberOfDays);

    return futureDate;
};

// The end date is the last day of access, so a grant ending today still has that day left and reads as zero days remaining.
// Both sides are midnight, so rounding absorbs the hour a daylight saving change would otherwise add or remove.
export const calculateNumberOfDaysRemaining = (expirationDate: Date): number =>
    Math.round((startOfDay(expirationDate).getTime() - startOfToday().getTime()) / numberOfMillisecondsInDay);

// Day based options always recalculate from today. Permanent has no end date and custom keeps the date the administrator picked.
export const calculateMarketAccessExpirationDate = (expirationOption: MarketAccessExpirationOption, customExpirationDate?: Date): Date | undefined => {
    if (expirationOption === 'custom') {
        return customExpirationDate;
    }

    const numberOfDays = numberOfDaysByExpirationOption[expirationOption];

    return numberOfDays === undefined ? undefined : addDaysToStartOfToday(numberOfDays);
};

export const createPermanentMarketAccessExpiration = (market: DemeterMarket): MarketAccessExpiration => ({
    market,
    expirationOption: 'permanent',
});

// Keeps the periods already set for markets that are still selected and starts every newly selected market at permanent.
export const synchronizeMarketAccessExpirations = (markets: DemeterMarket[], currentExpirations: MarketAccessExpiration[]): MarketAccessExpiration[] =>
    markets.map((market) => currentExpirations.find((expiration) => expiration.market === market) ?? createPermanentMarketAccessExpiration(market));

// The api sends the last day of access, so it is read as a plain calendar day rather than an instant.
export const parseMarketAccessEndDate = (endDate: string): Date | null => formattingService.parseDate(endDate.substring(0, 10), 'YYYY-MM-DD');

// Only the end date is persisted, so a saved grant comes back as either permanent or a custom date.
export const createMarketAccessExpirationsFromMarketAccesses = (
    markets: DemeterMarket[],
    marketAccesses: DemeterMarketAccessModel[],
): MarketAccessExpiration[] =>
    markets.map((market) => {
        const activeMarketAccess = marketAccesses.find(
            (marketAccess) => marketAccess.market === market && marketAccess.status === DemeterMarketAccessStatus.Active,
        );

        if (!activeMarketAccess?.endDate) {
            return createPermanentMarketAccessExpiration(market);
        }

        const expirationDate = parseMarketAccessEndDate(activeMarketAccess.endDate);

        return expirationDate ? { market, expirationOption: 'custom' as const, expirationDate } : createPermanentMarketAccessExpiration(market);
    });

// An undated grant sorts last so it never decides which occurrence is the most recent one.
const compareMarketAccessPastGrantsByEndDateDescending = (first: MarketAccessPastGrant, second: MarketAccessPastGrant): number =>
    (second.endDate?.getTime() ?? 0) - (first.endDate?.getTime() ?? 0);

// An expired sector is not in the selection, so its grant is read from the grant list rather than from the selection or it
// would vanish from the page. Only the most recent grant per sector is shown until the full history is built. Expired is
// matched rather than "not active" so that a status added later has to be considered instead of silently rendering as history.
export const createMarketAccessPastGrants = (selectedMarkets: DemeterMarket[], marketAccesses: DemeterMarketAccessModel[]): MarketAccessPastGrant[] =>
    marketAccesses
        .filter((marketAccess) => marketAccess.status === DemeterMarketAccessStatus.Expired && !selectedMarkets.includes(marketAccess.market))
        .map((marketAccess) => ({
            market: marketAccess.market,
            status: marketAccess.status,
            endDate: (marketAccess.endDate ? parseMarketAccessEndDate(marketAccess.endDate) : null) ?? undefined,
        }))
        .sort(compareMarketAccessPastGrantsByEndDateDescending)
        .filter((pastGrant, index, pastGrants) => pastGrants.findIndex((other) => other.market === pastGrant.market) === index);

// A market left permanent is simply absent from the dictionary, which the api reads as no end date.
export const buildMarketExpirationsRequest = (expirations: MarketAccessExpiration[]): AddDemeterUserRequestMarketExpirations => {
    const marketExpirations: AddDemeterUserRequestMarketExpirations = {};

    expirations.forEach((expiration) => {
        if (expiration.expirationOption === 'permanent' || !expiration.expirationDate) {
            return;
        }

        marketExpirations[expiration.market] = formattingService.toApiDate(expiration.expirationDate);
    });

    return marketExpirations;
};

export const collectPermanentMarkets = (expirations: MarketAccessExpiration[]): DemeterMarket[] =>
    expirations.filter((expiration) => expiration.expirationOption === 'permanent').map((expiration) => expiration.market);

// A sector that is already granted with no end date is not a new permanent grant, so the administrator is not warned about it again.
export const collectMarketsGrantedWithoutEndDate = (
    expirations: MarketAccessExpiration[],
    marketsAlreadyGrantedWithoutEndDate: DemeterMarket[],
): DemeterMarket[] => collectPermanentMarkets(expirations).filter((market) => !marketsAlreadyGrantedWithoutEndDate.includes(market));

export const hasIncompleteMarketAccessExpiration = (expirations: MarketAccessExpiration[]): boolean =>
    expirations.some((expiration) => expiration.expirationOption !== 'permanent' && !expiration.expirationDate);

export const areMarketAccessExpirationsEqual = (first: MarketAccessExpiration[], second: MarketAccessExpiration[]): boolean =>
    first.length === second.length &&
    first.every((expiration, index) => {
        const other = second[index];

        return (
            expiration.market === other.market &&
            expiration.expirationOption === other.expirationOption &&
            (expiration.expirationDate?.getTime() ?? null) === (other.expirationDate?.getTime() ?? null)
        );
    });
